import type { AppState, ImportSource, ISODate, LocalEvent, Overlay, Timing } from './model.ts';
import { addDays, compareDates, startDay } from './time.ts';
import { collectItems, deadlineStatus, type DeadlineStatus, type DisplayItem } from './views.ts';
import { commit, NotFoundError, type OpResult } from './ops.ts';
import { SKATTEFIX_SOURCE_ID } from './defaults.ts';
import type { Temporal } from './time.ts';

/**
 * Deadline Center ("Viktigt"). Vad som är en deadline bestäms uttryckligen – aldrig ur fri text:
 *  - Importerad post: källans inställning (ImportSource.treatAsDeadlines). Standard: ja för SkatteFix-
 *    exporten (en lista över skyldigheter), nej för andra kalenderfiler från t.ex. Google eller Outlook
 *    (ett importerat möte är ett vanligt kalenderbesök). Användaren kan ändra per källa och per post
 *    (Overlay.deadline = true/false); postens val går före källans.
 *  - Egen aktivitet: bara när användaren själv valt "Visa under Viktigt" (priority = 'high').
 *  - Länkad förberedelse (linkedSourceKey) är ingen egen deadline: den visas under sin källdeadline,
 *    så att samma sak aldrig räknas två gånger.
 * Datum passerat betyder inte misslyckat: AutoDay vet bara vad som markerats klart här.
 */

export function sourceTreatsAsDeadlines(source: Pick<ImportSource, 'id' | 'treatAsDeadlines'> | undefined, sourceId?: string): boolean {
  if (source?.treatAsDeadlines !== undefined) return source.treatAsDeadlines;
  return (source?.id ?? sourceId) === SKATTEFIX_SOURCE_ID;
}

export function isDeadline(state: AppState, item: Pick<DisplayItem, 'ref' | 'priority' | 'linkedSourceKey'>): boolean {
  if (item.ref.kind === 'local') return item.priority === 'high' && !item.linkedSourceKey;
  const key = item.ref.key;
  const choice = state.overlays[key]?.deadline;
  if (choice === true || choice === false) return choice;
  const src = state.sourceItems[key];
  if (!src) return false;
  return sourceTreatsAsDeadlines(state.sources[src.sourceId], src.sourceId);
}

export type DeadlineGroup = 'review' | 'passed' | 'soon' | 'later' | 'done';
export type DeadlineNext = 'review' | 'confirm' | 'prepare' | 'prepared' | 'open';

export interface PrepLink {
  id: string;
  title: string;
  timing: Timing;
  date: ISODate;
  done: boolean;
}

export interface DeadlineEntry {
  item: DisplayItem;
  status: DeadlineStatus;
  group: DeadlineGroup;
  origin: 'imported' | 'personal';
  /** Planerade förberedelser (länkade egna aktiviteter). Tom för återkommande källposter (manuellt flöde). */
  prep: PrepLink[];
  /** Källposten är återkommande: förberedelser länkas inte automatiskt till en viss förekomst. */
  recurringSource: boolean;
  next: DeadlineNext;
}

export interface DeadlineOverview {
  entries: DeadlineEntry[];
  counts: Record<DeadlineGroup, number>;
  /** Kräver uppmärksamhet: granskning + passerat utan klarmarkering + närmar sig (≤ SOON_DAYS). */
  attention: number;
  horizonDays: number;
}

/** "Närmar sig": idag och de kommande sju dagarna. */
export const SOON_DAYS = 7;

const GROUP_ORDER: DeadlineGroup[] = ['review', 'passed', 'soon', 'later', 'done'];

export function linkedPrep(state: AppState, key: string): PrepLink[] {
  const tz = state.settings.timeZone;
  return Object.values(state.events)
    .filter((e): e is LocalEvent => !e.deleted && e.linkedSourceKey === key)
    .map((e) => ({ id: e.id, title: e.title, timing: e.timing, date: startDay(e.timing, tz), done: !!e.done }))
    .sort((a, b) => compareDates(a.date, b.date));
}

/**
 * Samma underlag för Viktigt, Idag och räknare: varje deadlineförekomst finns i exakt en grupp.
 * Granskning går före allt annat (en ändrad källa kan ha nytt datum); passerat räknas bara inom
 * lookbackDays. Klarmarkerade framtida poster visas separat och räknas inte som uppmärksamhet.
 */
export function deadlineOverview(state: AppState, today: ISODate, horizonDays = 120, lookbackDays = 60): DeadlineOverview {
  const tz = state.settings.timeZone;
  const entries: DeadlineEntry[] = [];
  for (const item of collectItems(state, addDays(today, -lookbackDays), addDays(today, horizonDays))) {
    if (!isDeadline(state, item)) continue;
    const status = deadlineStatus(startDay(item.timing, tz), today, item.done);
    if (status.urgency === 'done' && status.daysLeft < 0) continue;
    const imported = item.ref.kind === 'source';
    const key = imported ? (item.ref as { key: string }).key : null;
    const recurringSource = !!(key && (state.sourceItems[key]?.data.recurrence || item.recurring));
    const prep = key && !recurringSource ? linkedPrep(state, key) : [];
    const group: DeadlineGroup = item.review
      ? 'review'
      : status.urgency === 'done'
        ? 'done'
        : status.urgency === 'passed'
          ? 'passed'
          : status.daysLeft <= SOON_DAYS
            ? 'soon'
            : 'later';
    const next: DeadlineNext =
      group === 'review' ? 'review' : group === 'passed' ? 'confirm' : group === 'done' || !imported || recurringSource ? 'open' : prep.some((p) => !p.done) ? 'prepared' : 'prepare';
    entries.push({ item, status, group, origin: imported ? 'imported' : 'personal', prep, recurringSource, next });
  }
  entries.sort((a, b) => {
    const g = GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group);
    if (g) return g;
    // Passerade: senast passerade först. Övriga: närmast först.
    return a.group === 'passed' ? b.status.daysLeft - a.status.daysLeft : a.status.daysLeft - b.status.daysLeft;
  });
  const counts: Record<DeadlineGroup, number> = { review: 0, passed: 0, soon: 0, later: 0, done: 0 };
  for (const e of entries) counts[e.group]++;
  return { entries, counts, attention: counts.review + counts.passed + counts.soon, horizonDays };
}

/** Användarens val för en importerad post: true/false, eller null = källans standard. Påverkar aldrig källdatum eller larm. */
export function setSourceDeadline(state: AppState, opId: string, key: string, value: boolean | null, now: Temporal.Instant): OpResult {
  const item = state.sourceItems[key];
  if (!item) throw new NotFoundError('Den importerade posten');
  const ov: Overlay = state.overlays[key] ?? { key, revision: 0 };
  const next: Overlay = { ...ov, deadline: value, revision: ov.revision + 1 };
  const label = value === true ? `Räknas som deadline: "${item.data.title}"` : value === false ? `Inte en deadline: "${item.data.title}"` : `Källans standard: "${item.data.title}"`;
  return commit(state, opId, label, { overlays: { [key]: next } }, now);
}

/** Källans standard för Viktigt. Ändrar bara inställningen – inga poster, datum eller anteckningar. */
export function setImportSourceDeadlines(state: AppState, sourceId: string, value: boolean): AppState {
  const src = state.sources[sourceId];
  if (!src) throw new NotFoundError('Importkällan');
  return { ...state, sources: { ...state.sources, [sourceId]: { ...src, treatAsDeadlines: value } } };
}
